import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import communityService from "../../../api/communityService";
import { setPostUsage } from "../../../redux/slices/communityAllowanceSlice";
import {
  mapPostsFromAPI,
  mapLikeResponseFromAPI,
  updatePostWithLikeData,
  updatePostWithCommentCount,
  mapNewPostFromAPI,
} from "../adapters";
import { toast } from "sonner";
import { generateAvatarUrl, getFullImageUrl } from "../utils/communityHelpers";
import { getUser } from "../../../utils/auth";

/**
 * Custom Hook: usePosts
 * Manages posts state and operations (fetch, create, like, delete)
 * Provides clean interface for components to interact with posts data
 */
const usePosts = () => {
  const dispatch = useDispatch();
  // Get current user from Redux store
  const currentUser = useSelector((state) => state.auth?.user);

  // State management
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    length: 10,
    total: 0,
    pages: 0,
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  /**
   * Fetch posts with filters and pagination
   * @param {Object} params - Query parameters
   * @param {boolean} append - If true, append to existing posts (for infinite scroll)
   */
  const fetchPosts = useCallback(async (params = {}, append = false) => {
    try {
      setLoading(true);
      setError(null);
      setHasInitialized(true);

      const queryParams = {
        page: params.page || pagination.page,
        length: params.length || pagination.length,
        ...(params.search && { search: params.search }),
        ...(params.category && { category: params.category }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.feed && { feed: params.feed }),
        ...(Array.isArray(params.roles) &&
          params.roles.length > 0 && { roles: params.roles }),
        ...(Array.isArray(params.specialities) &&
          params.specialities.length > 0 && {
            specialities: params.specialities,
          }),
        ...(params.location && { location: params.location }),
      };

      const response = await communityService.getAllPosts(queryParams);

      if (response?.data?.data?.posts) {
        const {
          data: postsData,
          total,
          page,
          length: perPage,
          pages,
        } = response.data.data.posts;

        // Map API response to UI format
        const mappedPosts = mapPostsFromAPI(postsData || []);

        // Filter out any null/undefined posts
        const validPosts = mappedPosts.filter((post) => post && post.id);

        // Either replace or append posts based on mode
        setPosts((prevPosts) => {
          if (append) {
            // Infinite scroll: append new posts, avoiding duplicates
            const existingIds = new Set(prevPosts.map((p) => p.id));
            const newPosts = validPosts.filter((p) => !existingIds.has(p.id));
            
            // If we have new posts, invalidate follow cache
            if (newPosts.length > 0 && window.invalidateFollowCache) {
              window.invalidateFollowCache();
            }
            
            return [...prevPosts, ...newPosts];
          } else {
            // Normal fetch: replace all posts
            // Invalidate follow cache when posts are refreshed/changed
            if (validPosts.length > 0 && window.invalidateFollowCache) {
              window.invalidateFollowCache();
            }
            
            return validPosts;
          }
        });

        setPagination({
          page: page || 1,
          length: perPage || 10,
          total: total || 0,
          pages: pages || 0,
        });
      } else {
        // No posts data in response
        if (!append) {
          setPosts([]);
        }
      }
    } catch (err) {
      console.error("Error fetching posts:", err);
      // Don't show error toast if it's an auth error (interceptor will handle redirect)
      if (err.message !== "Token expired. Please login again.") {
        setError(err?.response?.data?.message || "Failed to load posts");
        toast.error("Failed to load posts. Please try again.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - use params and pagination from closure

  /**
   * Create a new post with optimistic UI update (Facebook-level)
   * @param {FormData} postFormData - Post data including images
   * @param {string} categoryName - Category name for optimistic UI
   * @returns {Promise<Object>} - Success/failure result
   */
  const createPost = useCallback(
    async (postFormData, categoryName = "General") => {
      // Generate temporary ID for optimistic post
      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      let optimisticPost = null;

      try {
        // Step 1: Extract data from FormData for optimistic post
        const title = postFormData.get("title");
        const content = postFormData.get("content");
        const categoryId = postFormData.get("category");
        const link_preview = postFormData.get("link_preview");
        console.log(link_preview, "link_preview");
        const isAnonymous = postFormData.get("is_post_anonymously") === "1";

        // Extract image files for preview
        const imageFiles = [];
        const imageUrls = [];
        for (let [key, value] of postFormData.entries()) {
          if (key === "post_images" && value instanceof File) {
            imageFiles.push(value);
            // Create temporary preview URL
            const previewUrl = URL.createObjectURL(value);
            imageUrls.push(previewUrl);
          }
        }

        // Step 2: Get user info for optimistic post
        // CRITICAL: Use localStorage as fallback if Redux state not loaded (e.g., just after login)
        const localUser = getUser();

        // Use Redux currentUser if available, otherwise fallback to localStorage
        const userSource = currentUser || localUser || {};
        const userName = userSource?.full_name || userSource?.username || "You";
        const userImage = userSource?.profile_photo_path
          ? // ? `${process.env.REACT_APP_API_BASE_URL}/${userSource.profile_photo_path}`
            `${userSource.profile_photo_path}`
          : generateAvatarUrl(userName);

        // Step 3: Create optimistic post object
        optimisticPost = {
          id: tempId,
          author: userName,
          authorImage: userImage,
          time: "Just now",
          title: title || "",
          content: content || "",
          image: imageUrls.length > 0 ? imageUrls[0] : null,
          likes: 0,
          comments: 0,
          shares: 0,
          link_preview: link_preview,
          category: categoryId,
          categoryTag: categoryName,
          post_images: imageUrls.map((url, index) => ({
            id: `temp_img_${url.id}`,
            image_path: url,
            _isBlob: true,
          })),
          liked: false,
          userReaction: null,
          reactions: {
            like: 0,
            love: 0,
            haha: 0,
            wow: 0,
            angry: 0,
          },
          reactionDetails: [],
          commentedUsers: [],
          commentsList: [],
          _optimistic: true, // Mark as optimistic
          _tempId: tempId,
          _metadata: {
            isAnonymous: isAnonymous,
            postCategoryId: categoryId,
            createdAt: new Date().toISOString(),
          },
        };

        setPosts((prevPosts) => {
          const newPosts = [optimisticPost, ...prevPosts];

          return newPosts;
        });

        // Step 5: Call API (backend creates the real post)
        setLoading(true);
        setError(null);

        const response = await communityService.createPost(postFormData);

        // Step 6: Replace optimistic post with real post from server
        if (response?.data?.data) {
          // Update the free-tier weekly allowance from the server's fresh count.
          if (response.data.data.usage) {
            dispatch(setPostUsage(response.data.data.usage));
          }
          // Pass user data and category to preserve them from optimistic post
          const userData = {
            name: userName,
            image: userImage,
            isAnonymous: isAnonymous,
          };
          const realPost = mapNewPostFromAPI(response, userData, categoryName);

          if (realPost) {
            setPosts((prevPosts) => {
              return prevPosts.map((post) => {
                if (post._tempId === tempId) {
                  const mergedPost = {
                    ...post, // keep optimistic data (author, etc.)
                    ...realPost, // overlay backend fields once
                    title: realPost.title || post.title,
                    category: realPost.category, // ✅ numeric category from backend
                    categoryTag: realPost.categoryTag || post.categoryTag,
                    link_preview: realPost.link_preview,
                    post_images: Array.isArray(realPost.post_images)
                      ? realPost.post_images.map((img) => ({
                          ...img,
                          image_path: img.image_path,
                        }))
                      : post.post_images,
                    _optimistic: false,
                    _tempId: undefined,
                    image: imageUrls[0], // keep temporary blob until refresh
                    _needsRefresh: true,
                  };

                  return mergedPost;
                }

                return post;
              });
            });

            // Clean up preview URLs AFTER state update
            // Use setTimeout to ensure React has re-rendered with the new image URL
            // Only revoke if we got a real image URL from API
            if (realPost.image) {
              setTimeout(() => {
                imageUrls.forEach((url) => {
                  try {
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    console.warn("Failed to revoke URL:", e);
                  }
                });
              }, 100);
            } else {
            }

            toast.success("Post created successfully!");
            return { success: true, data: response.data.data };
          } else {
            console.warn("⚠️ [CREATE POST] Real post mapping returned null!");
            console.warn("⚠️ [CREATE POST] Keeping optimistic post in place");
            toast.success("Post created!");
            return { success: true, data: response.data.data };
          }
        }

        return { success: true };
      } catch (err) {
        console.error("❌ [CREATE POST] Error:", err);
        const errorMessage =
          err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          "Failed to create post";
        setError(errorMessage);

        // Weekly post allowance exhausted → sync the counter from the server.
        if (
          err?.response?.data?.errorType === "COMMUNITY_POST_LIMIT_REACHED" &&
          err?.response?.data?.data
        ) {
          dispatch(setPostUsage(err.response.data.data));
        }

        // Step 7: ROLLBACK - Remove optimistic post on error
        if (optimisticPost) {
          setPosts((prevPosts) =>
            prevPosts.filter((post) => post._tempId !== tempId),
          );
        }

        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [currentUser],
  );

  /**
   * Like or unlike a post with optimistic updates
   * CRITICAL: Only rollback on error, DON'T update on success!
   * @param {number} postId - Post ID
   */
  const toggleLikePost = useCallback(async (postId) => {
    // Prevent liking optimistic posts (those with temp IDs)
    if (typeof postId === "string" && postId.startsWith("temp_")) {
      console.warn(
        "⚠️ [POST LIKE] Cannot like optimistic post with temp ID:",
        postId,
      );
      toast.info("Please wait for post to finish uploading...");
      return;
    }

    // Store previous state for rollback
    let previousPost = null;

    try {
      // Step 1: Optimistic update (THIS IS FINAL!)
      setPosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post.id === postId) {
            // Double-check it's not an optimistic post
            if (post._optimistic) {
              console.warn("⚠️ [POST LIKE] Skipping optimistic post");
              return post;
            }

            // Capture previous state for rollback
            previousPost = {
              liked: post.liked,
              likes: post.likes,
              userReaction: post.userReaction,
              reactions: { ...post.reactions },
            };

            const newLiked = !post.liked;
            const newLikes = Math.max(0, post.likes + (newLiked ? 1 : -1));

            return {
              ...post,
              liked: newLiked,
              likes: newLikes,
              userReaction: newLiked ? "like" : null,
              reactions: {
                ...post.reactions,
                like: newLikes,
              },
            };
          }
          return post;
        }),
      );

      // Step 2: Call API (DON'T update state on success!)
      const response = await communityService.likeUnlikePost(postId);

      // SUCCESS: Do nothing! Optimistic update is already correct.
    } catch (err) {
      // Step 3: ONLY on error - rollback
      console.error("❌ [POST ERROR] Like toggle failed:", err);

      // Only show error toast if it's not a validation error (which we already prevented)
      const errorMessage =
        err?.response?.data?.message || "Failed to update like";
      if (!errorMessage.includes("must be a number")) {
        toast.error("Failed to update like");
      }

      // Rollback to previous state
      if (previousPost) {
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, ...previousPost } : post,
          ),
        );
      }
    }
  }, []);

  /**
   * Delete a post
   * @param {number} postId - Post ID
   */
  const deletePost = useCallback(async (postId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.deletePost(postId);

      if (response?.data) {
        // Remove post from state
        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));
        toast.success("Post deleted successfully");
        return { success: true };
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      const errorMessage =
        err?.response?.data?.message || "Failed to delete post";
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Report a post
   * @param {number} postId - Post ID
   * @param {string} reason - Report reason
   */
  const reportPost = useCallback(async (postId, reason) => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.reportPost(postId, reason);

      if (response?.data) {
        // Remove reported post from view
        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId));
        toast.success("Post reported successfully");
        return { success: true };
      }
    } catch (err) {
      console.error("Error reporting post:", err);
      const errorMessage =
        err?.response?.data?.message || "Failed to report post";
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update comment count for a post
   * Used when comments are added/deleted
   * @param {number} postId - Post ID
   * @param {number} newCount - New comment count
   */
  const updateCommentCount = useCallback((postId, newCount) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postId ? updatePostWithCommentCount(post, newCount) : post,
      ),
    );
  }, []);

  /**
   * Add or update emoji reaction to a post with optimistic updates
   * HYBRID APPROACH: Tracks legacy likes baseline for cross-platform compatibility
   * CRITICAL: Only rollback on error, DON'T sync with API on success!
   * @param {number} postId - Post ID
   * @param {string} reactionType - Reaction type: 'like', 'love', 'haha', 'wow', 'sad', 'angry'
   */
  const addOrUpdateReaction = useCallback(async (postId, reactionType) => {
    // Prevent reacting to optimistic posts
    if (typeof postId === "string" && postId.startsWith("temp_")) {
      console.warn("⚠️ [REACTION] Cannot react to optimistic post:", postId);
      toast.info("Please wait for post to finish uploading...");
      return;
    }

    // Store previous state for rollback
    let previousState = null;

    try {
      // Step 1: Optimistic update with legacy baseline tracking
      setPosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post.id === postId) {
            if (post._optimistic) {
              console.warn("⚠️ [REACTION] Skipping optimistic post");
              return post;
            }

            // Capture previous state
            previousState = {
              userReaction: post.userReaction,
              reactions: { ...post.reactions },
              likes: post.likes,
            };

            // HYBRID CALCULATION: Calculate baseline legacy likes (from mobile app)
            // These are likes from the old system that exist in posts.likes_count
            const currentReactionTotal = Object.values(post.reactions).reduce(
              (sum, count) => sum + count,
              0,
            );
            const legacyLikesBaseline = Math.max(
              0,
              post.likes - currentReactionTotal,
            );

            // If clicking same reaction, remove it
            if (post.userReaction === reactionType) {
              const newReactions = { ...post.reactions };
              newReactions[reactionType] = Math.max(
                0,
                (newReactions[reactionType] || 0) - 1,
              );
              const newReactionTotal = Object.values(newReactions).reduce(
                (sum, count) => sum + count,
                0,
              );
              const newTotalLikes = newReactionTotal + legacyLikesBaseline;

              return {
                ...post,
                userReaction: null,
                reactions: newReactions,
                likes: newTotalLikes,
                liked: false,
              };
            }

            // Otherwise, update or add reaction
            const newReactions = { ...post.reactions };

            // Remove old reaction count if switching
            if (post.userReaction && post.userReaction !== reactionType) {
              newReactions[post.userReaction] = Math.max(
                0,
                (newReactions[post.userReaction] || 0) - 1,
              );
            }

            // Add new reaction count
            newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;
            const newReactionTotal = Object.values(newReactions).reduce(
              (sum, count) => sum + count,
              0,
            );
            const newTotalLikes = newReactionTotal + legacyLikesBaseline;

            return {
              ...post,
              userReaction: reactionType,
              reactions: newReactions,
              likes: newTotalLikes,
              liked: true,
            };
          }
          return post;
        }),
      );

      // Step 2: Call API
      const response = await communityService.addOrUpdateReaction(
        postId,
        reactionType,
      );

      // console.log('✅ [REACTION SUCCESS] API response:', response?.data);

      // SUCCESS: Do nothing! Optimistic update is already correct.
      // The database now has the correct data, and on refresh it will load correctly.
    } catch (err) {
      console.error("❌ [REACTION ERROR] Failed:", err);
      toast.error("Failed to update reaction");

      // Rollback on error
      if (previousState) {
        console.log("🔴 [REACTION ROLLBACK] Reverting to previous state");
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId ? { ...post, ...previousState } : post,
          ),
        );
      }
    }
  }, []);

  /**
   * Load more posts (infinite scroll)
   * Appends next page to existing posts
   */
  const loadMore = useCallback(
    async (filters = {}) => {
      // Prevent loading if already at last page or currently loading
      if (loading || pagination.page >= pagination.pages) {
        console.log("📥 [LOAD MORE] Skipping - at end or loading", {
          loading,
          currentPage: pagination.page,
          totalPages: pagination.pages,
        });
        return;
      }

      const nextPage = pagination.page + 1;
      console.log("📥 [LOAD MORE] Loading page:", nextPage);

      await fetchPosts(
        {
          page: nextPage,
          ...filters,
        },
        true,
      ); // true = append mode
    },
    [fetchPosts, loading, pagination.page, pagination.pages],
  );

  /**
   * Refresh posts (reset to page 1)
   */
  const refresh = useCallback(
    async (filters = {}) => {
      await fetchPosts({ page: 1, ...filters }, false); // false = replace mode
    },
    [fetchPosts],
  );

  return {
    // State
    posts,
    loading,
    error,
    pagination,

    // Actions
    fetchPosts,
    createPost,
    toggleLikePost,
    deletePost,
    reportPost,
    updateCommentCount,
    addOrUpdateReaction,
    loadMore,
    refresh,

    // Helpers
    hasMore: pagination.page < pagination.pages,
  };
};

export default usePosts;
